import { useState } from 'react';
import { AlertCircle } from 'lucide-react';

import { productionApi } from 'api/production';
import ColorInput from 'components/storefront/StorefrontConfigEditor/components/ColorInput';
import EditableList, { ListItemDetail } from 'components/storefront/StorefrontConfigEditor/components/EditableList';
import ImageInput from 'components/storefront/StorefrontConfigEditor/components/ImageInput';
import { Alert, AlertDescription, AlertTitle } from 'shadcn/components/ui/Alert';
import { Input } from 'shadcn/components/ui/Input';
import { Label } from 'shadcn/components/ui/Label';
import { Textarea } from 'shadcn/components/ui/Textarea';
import CharacterCounter from 'shared/components/CharacterCounter';
import { CONTENT_LINK_URL_PATTERN, CONTENT_LINK_URL_TITLE } from 'shared/utils/contentLinkUrl';

const DISPLAY_NAME = 'Banner Carousel 1';

/**
 * Default configuration for a banner item in the carousel
 *
 * @constant {Object}
 * @property {string} imageUrl - Image URL
 * @property {string} name - Banner name
 * @property {string} description - Banner description
 * @property {string} linkText - Text for the banner link
 * @property {string} linkUrl - URL for the banner link
 * @property {string} bgColor - Background color in hex format
 */
const DEFAULT_BANNER_ITEM = Object.freeze({
    imageUrl: '',
    name: '',
    description: '',
    linkText: '',
    linkUrl: '',
    bgColor: '#F8F2ED', // Boysenberry "off-white"
});

/**
 * Default configuration for the BannerCarousel1 slice component
 *
 * @constant {Object}
 * @property {string} name - Name of the banner carousel
 * @property {Array} items - Array of banner items
 */
const DEFAULT_SLICE = Object.freeze({
    eyebrow: '',
    heading: '',
    name: '',
    items: [],
});

const MIN_BANNER_ITEMS = 3;

/**
 * Banner Carousel 1 slice component
 *
 * @param {Object} props
 * @param {boolean} props.disabled - Whether the component is disabled
 * @param {Function} props.onChange - Callback function for handling slice changes
 * @param {Object} props.slice - Slice configuration object
 * @returns {JSX.Element} BannerCarousel1 component
 */
function BannerCarousel1({ disabled, onChange, slice }) {
    const handleItemsChange = (updatedListItems) => {
        onChange({ ...slice, items: updatedListItems });
    };

    const handleNameChange = (e) => {
        onChange({ ...slice, name: e.target.value });
    };

    const handleEyebrowChange = (e) => {
        onChange({ ...slice, eyebrow: e.target.value });
    };

    const handleHeadingChange = (e) => {
        onChange({ ...slice, heading: e.target.value });
    };

    return (
        <div className="flex flex-col gap-6">
            {slice.items.length < MIN_BANNER_ITEMS && (
                <Alert variant="destructive">
                    <AlertCircle />
                    <AlertTitle>Warning</AlertTitle>
                    <AlertDescription>
                        A minimum of {MIN_BANNER_ITEMS} banner items are required for publication. Currently:{' '}
                        {slice.items.length}/{MIN_BANNER_ITEMS}
                    </AlertDescription>
                </Alert>
            )}

            <div className="flex flex-col gap-2">
                <Label htmlFor={`eyebrow-${slice.id}`}>Eyebrow</Label>
                <Input
                    type="text"
                    id={`eyebrow-${slice.id}`}
                    name="eyebrow"
                    value={slice.eyebrow ?? ''}
                    placeholder="Enter eyebrow"
                    onChange={handleEyebrowChange}
                    disabled={disabled}
                />
            </div>

            <div className="flex flex-col gap-2">
                <Label htmlFor={`heading-${slice.id}`}>Heading</Label>
                <Input
                    type="text"
                    id={`heading-${slice.id}`}
                    name="heading"
                    value={slice.heading ?? ''}
                    placeholder="Enter heading"
                    onChange={handleHeadingChange}
                    disabled={disabled}
                />
            </div>

            <div className="flex flex-col gap-1.5">
                <Label htmlFor={`name-${slice.id}`}>Name</Label>
                <p className="text-xs text-muted-foreground -mt-0.5">
                    For internal reference only. Not displayed to customers.
                </p>
                <Input
                    type="text"
                    id={`name-${slice.id}`}
                    name="name"
                    value={slice.name ?? ''}
                    placeholder="Enter carousel name"
                    onChange={handleNameChange}
                    disabled={disabled}
                />
            </div>

            <EditableList
                defaultItem={DEFAULT_BANNER_ITEM}
                disabled={disabled}
                ListItemForm={ListItemForm}
                ListItemPreview={ListItemPreview}
                listItems={slice.items}
                onChange={handleItemsChange}
                slice={slice}
            />
        </div>
    );
}

/**
 * Form component for editing individual banner items in the carousel
 *
 * @param {Object} props
 * @param {boolean} props.disabled - Whether the component is disabled
 * @param {number} props.index - Index of the current banner item
 * @param {Object} props.listItem - Current banner item data
 * @param {Function} props.onChange - Callback function for handling item changes
 * @param {Function} props.onInvalidInput - Callback function for handling invalid input
 * @param {Object} props.slice - Parent slice configuration object
 * @returns {JSX.Element} ListItemForm component
 */
function ListItemForm({ disabled, index, listItem, onChange, onInvalidInput, slice }) {
    const [imageError, setImageError] = useState(null);

    const handleChange = (e) => {
        onChange({ [e.target.name]: e.target.value });
    };

    const handleImageUpload = async (file) => {
        try {
            const response = await productionApi.uploadStorefrontConfigImage(file);
            const imageUrl = response.data.imagePath;
            onChange({ imageUrl });
            setImageError(null);
        } catch (e) {
            setImageError(`Error: ${e.message}`);
        }
    };

    const handleClearImage = () => {
        onChange({ imageUrl: '' });
    };

    return (
        <>
            <ImageInput
                id={`image-${slice.id}-${index}`}
                imageUrl={listItem.imageUrl}
                error={imageError}
                onClearImage={handleClearImage}
                onImageUpload={handleImageUpload}
                onInvalid={onInvalidInput}
                disabled={disabled}
            />

            <div className="flex flex-col gap-2">
                <Label htmlFor={`name-${slice.id}-${index}`}>Name</Label>
                <Input
                    type="text"
                    id={`name-${slice.id}-${index}`}
                    name="name"
                    value={listItem.name ?? ''}
                    placeholder="Enter name"
                    onChange={handleChange}
                    required
                    onInvalid={onInvalidInput}
                    disabled={disabled}
                    maxLength={25}
                />
                <CharacterCounter currentCount={listItem.name?.length ?? 0} maxLength={25} />
            </div>

            <div className="flex flex-col gap-2">
                <Label htmlFor={`description-${slice.id}-${index}`}>Description</Label>
                <Textarea
                    id={`description-${slice.id}-${index}`}
                    name="description"
                    value={listItem.description ?? ''}
                    placeholder="Enter description"
                    onChange={handleChange}
                    rows="3"
                    disabled={disabled}
                    maxLength={61}
                />
                <CharacterCounter currentCount={listItem.description?.length ?? 0} maxLength={61} />
            </div>

            <div className="grid grid-cols-2 gap-4">
                <div className="flex flex-col gap-2">
                    <Label htmlFor={`linkText-${slice.id}-${index}`}>Link Text</Label>
                    <Input
                        type="text"
                        id={`linkText-${slice.id}-${index}`}
                        name="linkText"
                        value={listItem.linkText ?? ''}
                        placeholder="Enter link text"
                        onChange={handleChange}
                        disabled={disabled}
                        maxLength={23}
                    />
                    <CharacterCounter currentCount={listItem.linkText?.length ?? 0} maxLength={23} />
                </div>

                <div className="flex flex-col gap-2">
                    <Label htmlFor={`linkUrl-${slice.id}-${index}`}>Link URL</Label>
                    <Input
                        type="text"
                        id={`linkUrl-${slice.id}-${index}`}
                        name="linkUrl"
                        value={listItem.linkUrl ?? ''}
                        placeholder="Enter link URL"
                        onChange={handleChange}
                        disabled={disabled}
                        pattern={CONTENT_LINK_URL_PATTERN}
                        title={CONTENT_LINK_URL_TITLE}
                    />
                </div>
            </div>

            <ColorInput
                id={`bgColor-${slice.id}-${index}`}
                label="Background Color"
                name="bgColor"
                value={listItem.bgColor}
                onChange={handleChange}
                disabled={disabled}
            />
        </>
    );
}

/**
 * Preview component for displaying banner item details
 *
 * @param {Object} props
 * @param {Object} props.listItem - Banner item data to preview
 * @returns {JSX.Element} ListItemPreview component
 */
function ListItemPreview({ listItem }) {
    return (
        <>
            <ListItemDetail label="Image" value={listItem} type="image" />
            <ListItemDetail label="Name" value={listItem.name} />
            <ListItemDetail label="Description" value={listItem.description} />
            <ListItemDetail label="Link Text" value={listItem.linkText} />
            <ListItemDetail label="Link URL" value={listItem.linkUrl} />
            <ListItemDetail label="Background Color" value={listItem.bgColor} type="bg-color" />
        </>
    );
}

const validateSlice = (slice, position) => {
    const errors = [];

    if (slice.items.length < MIN_BANNER_ITEMS) {
        errors.push(`${DISPLAY_NAME} (position ${position}) requires at least ${MIN_BANNER_ITEMS} banner items.`);
    }

    if (slice.items.some((item) => !item.imageUrl)) {
        errors.push(`${DISPLAY_NAME} (position ${position}) has missing images.`);
    }

    if (slice.items.some((item) => item.linkText && !item.linkUrl)) {
        errors.push(`${DISPLAY_NAME} (position ${position}) requires a link URL if link text is provided.`);
    }

    if (slice.items.some((item) => !item.linkText && item.linkUrl)) {
        errors.push(`${DISPLAY_NAME} (position ${position}) requires link text if a link URL is provided.`);
    }

    if (slice.eyebrow && !slice.heading) {
        errors.push(`${DISPLAY_NAME} (position ${position}) requires a heading when an eyebrow is provided.`);
    }

    return errors;
};

export default Object.assign(BannerCarousel1, {
    category: 'banner',
    defaultSlice: DEFAULT_SLICE,
    description: 'A carousel consisting of clickable banners containing an image, name, description, and link.',
    displayName: DISPLAY_NAME,
    thumbnail: 'https://img-cdn.misfitsmarket.com/test/lettuce/Minimap-Thumbnail__Banner-Carousel-1__Desktop',
    displayType: 'BANNER_CAROUSEL_1',
    validateSlice,
});
